import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmpContrato, EmpContratoInsert, STATUS_OPTIONS, TIPO_MEDICAO_OPTIONS, TIPO_CONTRATO_OPERACIONAL_OPTIONS, useEmpContratos } from '@/hooks/useEmpContratos';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { useClusters } from '@/hooks/useClusters';
import { Lock, Wand2, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { differenceInCalendarDays } from 'date-fns';
import { MunicipioAutocomplete } from './MunicipioAutocomplete';
import { MunicipioMultiSelect } from './MunicipioMultiSelect';
import { EmpContratoWizard } from './EmpContratoWizard';
import { useContratoMunicipios } from '@/hooks/useContratoMunicipios';

interface EmpContratoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editContrato?: EmpContrato | null;
}

const areStringArraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const buildContratoForm = (contrato: EmpContrato | null | undefined, defaultClusterId: string) => {
  if (contrato) {
    return {
      cluster_id: contrato.cluster_id,
      numero_contrato: contrato.numero_contrato,
      nome: contrato.nome,
      descricao: contrato.descricao || '',
      objeto: contrato.objeto || '',
      contratante: contrato.contratante,
      contratada: contrato.contratada,
      cnpj_contratada: contrato.cnpj_contratada || '',
      valor_global: String(contrato.valor_global),
      valor_original: String(contrato.valor_original ?? ''),
      data_assinatura: contrato.data_assinatura || '',
      data_inicio: contrato.data_inicio,
      data_fim_prevista: contrato.data_fim_prevista,
      status: contrato.status,
      tipo_medicao: contrato.tipo_medicao || 'preco_unitario',
      tipo_contrato_operacional: contrato.tipo_contrato_operacional || 'preco_unitario',
      ubs_preco_unitario: String(contrato.ubs_preco_unitario ?? ''),
      ubs_quantidade_contratada: String(contrato.ubs_quantidade_contratada ?? ''),
      municipio_ibge: contrato.municipio_ibge || '',
      percentual_retencao: String(contrato.percentual_retencao ?? '0'),
      data_base_reajuste: contrato.data_base_reajuste || '',
      prazo_reajuste: String(contrato.prazo_reajuste ?? ''),
    };
  }

  return {
    cluster_id: defaultClusterId,
    numero_contrato: '',
    nome: '',
    descricao: '',
    objeto: '',
    contratante: '',
    contratada: '',
    cnpj_contratada: '',
    valor_global: '',
    valor_original: '',
    data_assinatura: '',
    data_inicio: '',
    data_fim_prevista: '',
    status: 'rascunho',
    tipo_medicao: 'preco_unitario' as string,
    tipo_contrato_operacional: 'preco_unitario' as string,
    ubs_preco_unitario: '',
    ubs_quantidade_contratada: '',
    municipio_ibge: '',
    percentual_retencao: '0',
    data_base_reajuste: '',
    prazo_reajuste: '',
  };
};

export const EmpContratoDialog: React.FC<EmpContratoDialogProps> = ({
  open,
  onOpenChange,
  editContrato,
}) => {
  const { createContrato, updateContrato } = useEmpContratos();
  const { activeClusterIds } = useEmpFeatureFlag();
  const { clusters } = useClusters();
  const activeClusters = useMemo(
    () => clusters.filter(c => activeClusterIds.includes(c.id)),
    [clusters, activeClusterIds]
  );
  const defaultClusterId = activeClusters.length === 1 ? activeClusters[0].id : '';
  const { municipioCodes, syncMunicipios } = useContratoMunicipios(editContrato?.id);

  const isEdit = !!editContrato;
  const [cidadesExecucao, setCidadesExecucao] = useState<string[]>([]);
  const [wizardOpen, setWizardOpen] = useState(false);

  const [form, setForm] = useState(() => buildContratoForm(editContrato, defaultClusterId));

  useEffect(() => {
    if (!open) return;
    setForm(buildContratoForm(editContrato, defaultClusterId));
    setCidadesExecucao(editContrato ? [] : []);
  }, [open, editContrato?.id, defaultClusterId]);

  useEffect(() => {
    if (!open || !editContrato) return;
    setCidadesExecucao(current => (areStringArraysEqual(current, municipioCodes) ? current : municipioCodes));
  }, [open, editContrato, municipioCodes]);

  // ── Auto-compute prazo (days) ──
  const prazoDias = useMemo(() => {
    if (!form.data_inicio || !form.data_fim_prevista) return null;
    try {
      const start = new Date(form.data_inicio + 'T00:00:00');
      const end = new Date(form.data_fim_prevista + 'T00:00:00');
      const diff = differenceInCalendarDays(end, start) + 1;
      return diff > 0 ? diff : null;
    } catch {
      return null;
    }
  }, [form.data_inicio, form.data_fim_prevista]);

  // valor_global is always managed by the engine (PPU + aditivos), never manually editable

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isUbs = form.tipo_contrato_operacional === 'ordem_servico';
    if (isUbs && (!form.ubs_preco_unitario || !form.ubs_quantidade_contratada)) return;

    // Block new contracts without valor_original
    const parsedValorOriginal = Number(form.valor_original);
    if (!isEdit && (!form.valor_original || parsedValorOriginal <= 0)) return;

    const payload: EmpContratoInsert = {
      cluster_id: form.cluster_id,
      numero_contrato: form.numero_contrato,
      nome: form.nome,
      descricao: form.descricao || null,
      objeto: form.objeto || null,
      contratante: form.contratante,
      contratada: form.contratada,
      cnpj_contratada: form.cnpj_contratada || null,
      valor_global: isEdit ? (editContrato?.valor_global ?? 0) : parsedValorOriginal,
      valor_original: parsedValorOriginal,
      data_assinatura: form.data_assinatura || null,
      data_inicio: form.data_inicio,
      data_fim_prevista: form.data_fim_prevista,
      prazo_meses: prazoDias ? Math.round(prazoDias / 30) : null,
      tipo_medicao: form.tipo_medicao as 'preco_unitario' | 'epc' | 'hibrido',
      tipo_contrato_operacional: form.tipo_contrato_operacional as 'preco_unitario' | 'preco_global' | 'ordem_servico',
      ubs_preco_unitario: isUbs ? Number(form.ubs_preco_unitario) : 0,
      ubs_quantidade_contratada: isUbs ? Number(form.ubs_quantidade_contratada) : 0,
      municipio_ibge: form.municipio_ibge || null,
      percentual_retencao: Number(form.percentual_retencao) || 0,
      data_base_reajuste: form.data_base_reajuste || null,
      prazo_reajuste: form.prazo_reajuste ? Number(form.prazo_reajuste) : null,
    };

    if (isEdit && editContrato) {
      const updates: any = { ...payload, status: form.status };
      delete updates.valor_global;
      await updateContrato.mutateAsync({ id: editContrato.id, ...updates });
      await syncMunicipios.mutateAsync({ contratoId: editContrato.id, codes: cidadesExecucao });
    } else {
      const result = await createContrato.mutateAsync(payload);
      if (result?.id && cidadesExecucao.length > 0) {
        await syncMunicipios.mutateAsync({ contratoId: result.id, codes: cidadesExecucao });
      }
    }
    onOpenChange(false);
  };

  const isSubmitting = createContrato.isPending || updateContrato.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Contrato' : 'Novo Contrato de Empreendimento'}</DialogTitle>
          <DialogDescription>
            Preencha os dados do contrato e selecione os municípios de sede e execução.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Cluster */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cluster *</Label>
              <Select value={form.cluster_id} onValueChange={v => setForm(f => ({ ...f, cluster_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {activeClusters.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nº Contrato *</Label>
              <Input value={form.numero_contrato} onChange={e => setForm(f => ({ ...f, numero_contrato: e.target.value }))} required />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Nome do Contrato *</Label>
            <Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} required />
          </div>

          <div className="space-y-2">
            <Label>Objeto</Label>
            <Textarea value={form.objeto} onChange={e => setForm(f => ({ ...f, objeto: e.target.value }))} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contratante *</Label>
              <Input value={form.contratante} onChange={e => setForm(f => ({ ...f, contratante: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label>Contratada *</Label>
              <Input value={form.contratada} onChange={e => setForm(f => ({ ...f, contratada: e.target.value }))} required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CNPJ Contratada</Label>
              <Input value={form.cnpj_contratada} onChange={e => setForm(f => ({ ...f, cnpj_contratada: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                Valor Original (R$) *
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>Valor do contrato na data de assinatura. Não inclui aditivos ou reajustes.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={isEdit ? String(editContrato?.valor_original ?? editContrato?.valor_global ?? 0) : form.valor_original}
                onChange={isEdit ? undefined : (e) => setForm(f => ({ ...f, valor_original: e.target.value }))}
                disabled={isEdit}
                className={isEdit ? 'bg-muted' : ''}
                required={!isEdit}
              />
              <p className="text-xs text-muted-foreground">
                {isEdit
                  ? 'Valor jurídico do contrato na assinatura. Imutável após criação.'
                  : 'Informe o valor do contrato conforme assinado. Campo obrigatório.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Data Assinatura</Label>
              <Input type="date" value={form.data_assinatura} onChange={e => setForm(f => ({ ...f, data_assinatura: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Data Início *</Label>
              <Input type="date" value={form.data_inicio} onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label>Data Fim Prevista *</Label>
              <Input type="date" value={form.data_fim_prevista} onChange={e => setForm(f => ({ ...f, data_fim_prevista: e.target.value }))} required />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                Prazo (dias)
                <Lock className="h-3 w-3 text-muted-foreground" />
              </Label>
              <Input
                value={prazoDias != null ? `${prazoDias} dias` : '—'}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Calculado automaticamente (Data Fim − Data Início + 1)</p>
            </div>
            <div className="space-y-2">
              <Label>Tipo de Medição *</Label>
              {form.tipo_contrato_operacional === 'preco_unitario' ? (
                <>
                  <Input value="Por Item" disabled className="bg-muted" />
                  <p className="text-xs text-muted-foreground">
                    Contratos PPU utilizam exclusivamente medição por item.
                  </p>
                </>
              ) : (
                <Select
                  value={form.tipo_medicao}
                  onValueChange={v => setForm(f => ({ ...f, tipo_medicao: v }))}
                  disabled={isEdit && editContrato?.status !== 'rascunho'}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPO_MEDICAO_OPTIONS.filter(t => {
                      // For preco_global, show epc and hibrido
                      if (form.tipo_contrato_operacional === 'preco_global') return t.value !== 'preco_unitario';
                      return true;
                    }).map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <div>
                          <span>{t.label}</span>
                          <p className="text-xs text-muted-foreground font-normal">{t.description}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {isEdit && editContrato?.status !== 'rascunho' && form.tipo_contrato_operacional !== 'preco_unitario' && (
                <p className="text-xs text-muted-foreground">Bloqueado — somente editável em rascunho</p>
              )}
            </div>
            {isEdit && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Tipo de Contrato Operacional */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Tipo de Contrato Operacional *</Label>
                {!isEdit && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => setWizardOpen(true)}>
                    <Wand2 className="h-3 w-3" /> Assistente
                  </Button>
                )}
              </div>
              <Select
                value={form.tipo_contrato_operacional}
                onValueChange={v => {
                  const updates: Record<string, string> = { tipo_contrato_operacional: v };
                  // PPU → force tipo_medicao to preco_unitario
                  if (v === 'preco_unitario') updates.tipo_medicao = 'preco_unitario';
                  // Preço Global → default to epc if currently preco_unitario
                  if (v === 'preco_global' && form.tipo_medicao === 'preco_unitario') updates.tipo_medicao = 'epc';
                  setForm(f => ({ ...f, ...updates }));
                }}
                disabled={isEdit && editContrato?.status !== 'rascunho'}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_CONTRATO_OPERACIONAL_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isEdit && editContrato?.status !== 'rascunho' && (
                <p className="text-xs text-muted-foreground">Bloqueado — somente editável em rascunho</p>
              )}
            </div>
          </div>

          {/* Campos condicionais UBS */}
          {form.tipo_contrato_operacional === 'ordem_servico' && (
            <div className="grid grid-cols-2 gap-4 p-3 rounded-md border border-dashed border-primary/30 bg-primary/5">
              <div className="col-span-2">
                <p className="text-sm font-medium text-primary mb-2">Parâmetros UBS (Ordem de Serviço)</p>
              </div>
              <div className="space-y-2">
                <Label>Preço Unitário da UBS (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.ubs_preco_unitario}
                  onChange={e => setForm(f => ({ ...f, ubs_preco_unitario: e.target.value }))}
                  required
                  placeholder="Ex: 1250.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantidade Contratada (UBS) *</Label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={form.ubs_quantidade_contratada}
                  onChange={e => setForm(f => ({ ...f, ubs_quantidade_contratada: e.target.value }))}
                  required
                  placeholder="Ex: 500"
                />
              </div>
            </div>
          )}

          {/* Retenção e Reajuste */}
          <div className="grid grid-cols-3 gap-4 p-3 rounded-md border border-dashed border-muted-foreground/30 bg-muted/5">
            <div className="col-span-3">
              <p className="text-sm font-medium text-muted-foreground mb-2">Retenção e Reajuste</p>
            </div>
            <div className="space-y-2">
              <Label>Retenção (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.percentual_retencao}
                onChange={e => setForm(f => ({ ...f, percentual_retencao: e.target.value }))}
                placeholder="Ex: 5.00"
              />
              <p className="text-xs text-muted-foreground">Aplicado sobre valor líquido dos boletins</p>
            </div>
            <div className="space-y-2">
              <Label>Data-Base Reajuste</Label>
              <Input
                type="date"
                value={form.data_base_reajuste}
                onChange={e => setForm(f => ({ ...f, data_base_reajuste: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Aniversário para aplicação de índice</p>
            </div>
            <div className="space-y-2">
              <Label>Prazo Reajuste (meses)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                value={form.prazo_reajuste}
                onChange={e => setForm(f => ({ ...f, prazo_reajuste: e.target.value }))}
                placeholder="Ex: 12"
              />
              <p className="text-xs text-muted-foreground">Período em meses até o reajuste</p>
            </div>
          </div>


          <div className="space-y-2">
            <Label>
              Município Sede (Georreferenciamento)
              <span className="text-xs text-muted-foreground font-normal ml-1.5">— opcional</span>
            </Label>
            <MunicipioAutocomplete
              value={form.municipio_ibge}
              onChange={v => setForm(f => ({ ...f, municipio_ibge: v }))}
            />
            <p className="text-xs text-muted-foreground">
              Se não informado, o contrato herda as coordenadas do cluster.
            </p>
          </div>

          {/* Cidades de execução — Multi-select */}
          <div className="space-y-2">
            <Label>
              Cidades de Execução
              <span className="text-xs text-muted-foreground font-normal ml-1.5">— onde o contrato opera</span>
            </Label>
            <MunicipioMultiSelect
              values={cidadesExecucao}
              onChange={setCidadesExecucao}
            />
            <p className="text-xs text-muted-foreground">
              Selecione todas as cidades onde este contrato executará serviços.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Escopo Resumido</Label>
            <Textarea value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isSubmitting || !form.cluster_id || !form.numero_contrato || !form.nome}>
              {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Contrato'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <EmpContratoWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onComplete={(result) => {
          setForm(f => ({
            ...f,
            tipo_contrato_operacional: result.tipoContratoOperacional,
            tipo_medicao: result.tipoMedicao,
          }));
        }}
      />
    </Dialog>
  );
};
